import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { authService } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginForm = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);

    try {
      // Auto-login without credentials for development/testing
      const mockUser = {
        id: '1',
        email: data.email || 'demo@kreupai.com',
        firstName: 'Demo',
        lastName: 'User',
        role: 'admin',
        organizationId: '1',
      };
      const mockToken = 'mock-jwt-token-' + Date.now();
      
      login(mockUser, mockToken);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle auto-login on button click (bypasses form validation)
  const handleAutoLogin = () => {
    setIsLoading(true);
    
    const mockUser = {
      id: '1',
      email: 'demo@kreupai.com',
      firstName: 'Demo',
      lastName: 'User',
      role: 'admin',
      organizationId: '1',
    };
    const mockToken = 'mock-jwt-token-' + Date.now();
    
    login(mockUser, mockToken);
    toast.success('Login successful!');
    navigate('/dashboard');
    setIsLoading(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-neutral-900 mb-6">
        Sign in to your account
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Email address
          </label>
          <input
            {...register('email')}
            type="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-alert-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Password
          </label>
          <input
            {...register('password')}
            type="password"
            className={`input ${errors.password ? 'input-error' : ''}`}
            placeholder="••••••••"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-alert-600">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me & Forgot password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input type="checkbox" className="rounded text-primary-500" />
            <span className="ml-2 text-sm text-neutral-700">Remember me</span>
          </label>

          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit button */}
        <button
          type="button"
          onClick={handleAutoLogin}
          disabled={isLoading}
          className="btn btn-primary w-full"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <span className="spinner mr-2"></span>
              Signing in...
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      {/* Sign up link */}
      <p className="mt-6 text-center text-sm text-neutral-600">
        Don't have an account?{' '}
        <Link
          to="/register"
          className="font-medium text-primary-600 hover:text-primary-700"
        >
          Sign up
        </Link>
      </p>
    </div>
  );
};

export default Login;
